import { useState, useEffect } from 'react';
import axios from 'axios';
import { DownloadIcon, FileTextIcon } from 'lucide-react';
import { DataTable, EmptyState, Panel, Pill, SkeletonTable } from '@bcecloud/portal-ui';
import { getToken } from '@/lib/auth';
import { useEventContext } from './useEventContext';

/** One attachment row, as the attachments endpoint returns it. */
interface Attachment {
  _id: string;
  name?: string;
  description?: string;
  type?: string;
  createdAt?: string;
}

/**
 * Formats a date for display.
 *
 * @param dateStr - ISO date.
 * @returns e.g. "Aug 8, 2026".
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Returns a friendly file type label from a MIME type.
 *
 * @param mimeType - The stored MIME type.
 * @returns A short label like "PDF".
 */
function formatFileType(mimeType?: string): string {
  if (!mimeType) return '—';
  const map: Record<string, string> = {
    'application/pdf': 'PDF',
    'image/jpeg': 'Image',
    'image/jpg': 'Image',
    'image/png': 'Image',
    'video/mp4': 'Video',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
    'application/msword': 'Word',
    'application/vnd.ms-excel': 'Excel',
    'text/plain': 'Text',
  };
  return map[mimeType] || mimeType.split('/').pop()?.toUpperCase() || '—';
}

/**
 * Event documents tab — attachments with download links.
 */
export function EventDocumentsTab(): React.ReactElement {
  const { event } = useEventContext();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchAttachments(): Promise<void> {
      if (!event?._id) return;
      try {
        const token = getToken();
        // The V3 list scopes by role server-side: clients only ever receive
        // client-viewable documents for events they are a contact on.
        const { data } = await axios.get(`/api/v3/events/${event._id}/attachments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setAttachments(data.data || []);
      } catch {
        if (!cancelled) setAttachments([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void fetchAttachments();
    return () => {
      cancelled = true;
    };
  }, [event?._id]);

  if (loading) {
    return <SkeletonTable rows={3} columns={['w-[30%]', 'w-[15%]']} label="Loading documents…" />;
  }

  if (attachments.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon={<FileTextIcon aria-hidden className="size-6" />}
          title="No documents"
          message="No documents have been shared for this event yet. Contracts, agendas and handouts appear here."
        />
      </Panel>
    );
  }

  return (
    <DataTable
      headers={['Name', 'Type', 'Description', 'Uploaded', 'Actions']}
      visuallyHiddenHeaders={[4]}
      minWidth={720}
    >
      {attachments.map((attachment) => (
        <tr
          key={attachment._id}
          className="border-t border-portal-line align-middle text-portal-tiny"
        >
          <td className="px-4 py-3 text-portal-small font-bold">{attachment.name ?? '—'}</td>
          <td className="px-4 py-3">
            <Pill tone="neutral">{formatFileType(attachment.type)}</Pill>
          </td>
          <td className="max-w-[280px] truncate px-4 py-3 text-portal-muted">
            {attachment.description ?? '—'}
          </td>
          <td className="px-4 py-3 whitespace-nowrap text-portal-muted">
            {formatDate(attachment.createdAt)}
          </td>
          <td className="px-4 py-3 text-right whitespace-nowrap">
            <a
              href={`/api/v3/attachments/${attachment._id}/view`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-portal-micro font-bold text-brand-link hover:underline"
            >
              <DownloadIcon aria-hidden className="size-3.5" />
              Download
            </a>
          </td>
        </tr>
      ))}
    </DataTable>
  );
}
